import Aura from "@primeng/themes/aura";
import { definePreset } from "@primeng/themes";

export const MyPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: "0",
      xs: "8px",
      sm: "12px",
      md: "16px",
      lg: "24px",
      xl: "32px",
      full: "9999px",
    },
    red: {
      25: "#FFEDEF",
      400: "#FF626D",
      500: "#FC2B3A",
    },
    blue: {
      25: "#EBF4FF",
      500: "#2F79E9",
    },
    gray: {
      100: "#EEF2F6",
      200: "#E3E8EF",
      300: "#CDD5DF",
      400: "#9AA4B2",
      500: "#697586",
      600: "#4B5565",
      850: "#121926",
      900: "#0D121C",
    },
    base: {
      white: "#FDFDFD",
      black: "#0B0B0B",
    },
  },
  semantic: {
    transitionDuration: "0.2s",
    focusRing: {
      width: "1px",
      style: "solid",
      color: "{primary.color}",
      offset: "2px",
      shadow: "none",
    },
    disabledOpacity: "0.6",
    iconSize: "1rem",
    anchorGutter: "2px",
    primary: {
      25: "#E2F5F1",
      400: "#27AD8F",
      500: "#009877",
    },
    formField: {
      paddingX: "0.75rem",
      paddingY: "0.375rem",
      sm: {
        fontSize: "0.875rem",
        paddingX: "0.625rem",
        paddingY: "0.375rem",
      },
      lg: {
        fontSize: "1.125rem",
        paddingX: "0.875rem",
        paddingY: "0.625rem",
      },
      borderRadius: "{border.radius.xs}",
      focusRing: {
        width: "0",
        style: "none",
        color: "transparent",
        offset: "0",
        shadow: "none",
      },
      transitionDuration: "{transition.duration}",
    },
    list: {
      padding: "0.25rem 0.25rem",
      gap: "2px",
      header: {
        padding: "0.5rem 1rem 0.25rem 1rem",
      },
      option: {
        padding: "0.5rem 0.75rem",
        borderRadius: "{border.radius.sm}",
      },
      optionGroup: {
        padding: "0.5rem 0.75rem",
        fontWeight: "600",
      },
    },
    content: {
      borderRadius: "{border.radius.md}",
    },
    mask: {
      transitionDuration: "0.15s",
    },
    navigation: {
      list: {
        padding: "0.25rem 0.25rem",
        gap: "2px",
      },
      item: {
        padding: "0.5rem 0.75rem",
        borderRadius: "{border.radius.sm}",
        gap: "0.5rem",
      },
      submenuLabel: {
        padding: "0.5rem 0.75rem",
        fontWeight: "600",
      },
      submenuIcon: {
        size: "0.875rem",
      },
    },
    overlay: {
      select: {
        borderRadius: "{border.radius.md}",
        shadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
      },
      popover: {
        borderRadius: "{border.radius.md}",
        padding: "0.75rem",
        shadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
      },
      modal: {
        borderRadius: "{border.radius.xl}",
        padding: "1.5rem",
        shadow: "none",
      },
      navigation: {
        shadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
      },
    },

    colorScheme: {
      light: {
        surface: {
          primary: {
            color: "{primary.500}",
            contrastColor: "{base.white}",
            hoverColor: "{primary.400}",
            activeColor: "{primary.500}",
          },
          secondary: {
            color: "{gray.900}",
            hoverColor: "{gray.850}",
            activeColor: "{gray.900}",
          },
          tint: {
            color: "{primary.25}",
          },
          gray: {
            low: {
              color: "{base.white}",
            },
            medium: {
              color: "{gray.100}",
            },
            high: {
              color: "{gray.300}",
              hoverColor: "{gray.400}",
            },
          },
          error: {
            low: {
              color: "{red.25}",
            },
            high: {
              color: "{red.500}",
              hoverColor: "{red.400}",
            },
          },
          information: {
            low: {
              color: "{blue.25}",
            },
            high: {
              color: "{blue.500}",
            },
          },
          disabled: {
            color: "{gray.200}",
          },
        },
        primary: {
          color: "{primary.500}",
          contrastColor: "{base.white}",
          hoverColor: "{primary.400}",
          activeColor: "{primary.500}",
        },
        highlight: {
          background: "{primary.50}",
          focusBackground: "{primary.100}",
          color: "{primary.700}",
          focusColor: "{primary.800}",
        },
        mask: {
          background: "rgba(0,0,0,0.4)",
          color: "{surface.200}",
        },
        formField: {
          background: "{surface.gray.low.color}",
          disabledBackground: "{surface.200}",
          filledBackground: "{surface.gray.low.color}",
          filledHoverBackground: "{surface.gray.low.color}",
          filledFocusBackground: "{surface.gray.low.color}",
          borderColor: "{gray.200}",
          hoverBorderColor: "{gray.200}",
          focusBorderColor: "{primary.color}",
          invalidBorderColor: "{red.500}",
          color: "{gray.900}",
          disabledColor: "{surface.500}",
          placeholderColor: "{surface.500}",
          invalidPlaceholderColor: "{red.600}",
          floatLabelColor: "{gray.500}",
          floatLabelFocusColor: "{gray.500}",
          floatLabelActiveColor: "{gray.500}",
          floatLabelInvalidColor: "{gray.500}",
          iconColor: "{surface.400}",
          shadow: "none",
        },
        text: {
          color: "{surface.700}",
          hoverColor: "{surface.800}",
          mutedColor: "{surface.500}",
          hoverMutedColor: "{surface.600}",
        },
        content: {
          background: "{surface.0}",
          hoverBackground: "{surface.100}",
          borderColor: "{surface.200}",
          color: "{text.color}",
          hoverColor: "{text.hover.color}",
        },
        overlay: {
          select: {
            background: "{surface.0}",
            borderColor: "{surface.200}",
            color: "{text.color}",
          },
          popover: {
            background: "{surface.0}",
            borderColor: "{surface.200}",
            color: "{text.color}",
          },
          modal: {
            background: "{surface.0}",
            borderColor: "{surface.200}",
            color: "{text.color}",
          },
        },
        list: {
          option: {
            focusBackground: "{surface.100}",
            selectedBackground: "{highlight.background}",
            selectedFocusBackground: "{highlight.focus.background}",
            color: "{text.color}",
            focusColor: "{text.hover.color}",
            selectedColor: "{highlight.color}",
            selectedFocusColor: "{highlight.focus.color}",
            icon: {
              color: "{surface.400}",
              focusColor: "{surface.500}",
            },
          },
          optionGroup: {
            background: "transparent",
            color: "{text.muted.color}",
          },
        },
        navigation: {
          item: {
            focusBackground: "{surface.100}",
            activeBackground: "{surface.100}",
            color: "{text.color}",
            focusColor: "{text.hover.color}",
            activeColor: "{text.hover.color}",
            icon: {
              color: "{surface.400}",
              focusColor: "{surface.500}",
              activeColor: "{surface.500}",
            },
          },
          submenuLabel: {
            background: "transparent",
            color: "{text.muted.color}",
          },
          submenuIcon: {
            color: "{surface.400}",
            focusColor: "{surface.500}",
            activeColor: "{surface.500}",
          },
        },
      },
    },
  },
  components: {
    // dialog: {
    //   header: {
    //     padding: "1.5rem 0 0 0",
    //   },
    // },
    fileupload: {
      border: {
        radius: "{border.radius.sm}",
        color: "{gray.300}",
      },
    },
  },
});
